/**
 * SINQUE — UI engine (non-cart).
 * Ported from the Astro client.ts: cart drawer open/close, full-screen mobile
 * menu, and the size-selector radio-group. The AJAX cart logic lives in cart.js;
 * this file owns the overlay/focus/inert behaviour and the size selector.
 *
 * Shared DOM hooks (must match markup + cart.js exactly):
 *   [data-cart-open] [data-cart-drawer] [data-cart-overlay] [data-cart-close]
 *   [data-nav-toggle] [data-nav-toggle-label] [data-mobile-nav]
 *   .size-selector__options[role=radiogroup] .size-selector__option[role=radio]
 *   .size-selector__label#size-label[aria-live=polite]
 *
 * Lenis: when an overlay opens we call lenis.stop() so the background can't
 * scroll behind it, and lenis.start() on close. Guarded if Lenis is absent.
 */
(function () {
  'use strict';

  // ---------- module-scoped state ----------
  var lastFocusedEl = null;
  var drawerKeysBound = false;
  var menuKeysBound = false;

  // ---------- Lenis access (motion engine exposes window.SinqueMotion) ----------
  function lenisStop() {
    if (window.SinqueMotion && window.SinqueMotion.stopScroll) window.SinqueMotion.stopScroll();
  }
  function lenisStart() {
    if (window.SinqueMotion && window.SinqueMotion.startScroll) window.SinqueMotion.startScroll();
  }

  // =============================================================
  // CART DRAWER
  // =============================================================

  // Make everything except the drawer + overlay non-interactive while the dialog
  // is open (background can't be tabbed into or read by a virtual cursor).
  function setPageInert(on) {
    // The drawer + overlay are output by {% section %}, which Shopify wraps in a
    // <div class="shopify-section">. So they're nested, not direct body children.
    // Skip the drawer/overlay AND any wrapper that contains them — otherwise the
    // wrapper gets `inert` and the whole dialog inherits it, killing the Close
    // button and overlay clicks (only Escape would still work).
    var drawer = document.querySelector('[data-cart-drawer]');
    var overlay = document.querySelector('[data-cart-overlay]');
    var kids = document.querySelectorAll('body > *');
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el.matches('script, style, link')) continue;
      if (drawer && (el === drawer || el.contains(drawer))) continue;
      if (overlay && (el === overlay || el.contains(overlay))) continue;
      el.inert = on;
    }
  }

  function getDrawer() { return document.querySelector('[data-cart-drawer]'); }

  function isDrawerOpen() {
    var d = getDrawer();
    return !!(d && d.classList.contains('is-open'));
  }

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    lastFocusedEl = document.activeElement;
    drawer.classList.add('is-open');
    var overlay = document.querySelector('[data-cart-overlay]');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer.inert = false; // re-enable the dialog before focusing into it
    drawer.setAttribute('aria-hidden', 'false');
    setPageInert(true);
    lenisStop();
    // Move focus into the dialog for keyboard + screen-reader users.
    var closeBtn = drawer.querySelector('[data-cart-close]');
    (closeBtn || drawer).focus();
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    var overlay = document.querySelector('[data-cart-overlay]');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setPageInert(false);
    drawer.inert = true; // closed drawer is off-screen; keep it out of the tab order
    drawer.setAttribute('aria-hidden', 'true');
    lenisStart();
    // Restore focus to whatever opened the drawer.
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') lastFocusedEl.focus();
    lastFocusedEl = null;
  }

  function focusableInDrawer() {
    var drawer = getDrawer();
    if (!drawer) return [];
    var nodes = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null;
    });
  }

  function wireDrawer() {
    // Drawer open buttons (header Bag, etc.). Use a guarded delegate so the
    // handler survives header/section re-renders without double-binding.
    document.querySelectorAll('[data-cart-open]').forEach(function (el) {
      if (el.dataset.cartOpenWired) return;
      el.dataset.cartOpenWired = '1';
      el.addEventListener('click', openDrawer);
    });

    // Close button + overlay live inside the cart-drawer section, which is
    // re-rendered on every add/change. Delegate from document so they keep
    // working after a section innerHTML swap.
    if (!drawerKeysBound) {
      drawerKeysBound = true;

      document.addEventListener('click', function (e) {
        var t = e.target;
        if (t.closest && t.closest('[data-cart-close]')) { closeDrawer(); return; }
        // Overlay click closes (only when clicking the overlay itself).
        if (t.matches && t.matches('[data-cart-overlay]')) { closeDrawer(); }
      });

      // Document-level keyboard handling — bound once (document persists).
      document.addEventListener('keydown', function (e) {
        if (!isDrawerOpen()) return;
        if (e.key === 'Escape') { closeDrawer(); return; }
        if (e.key === 'Tab') {
          var items = focusableInDrawer();
          if (items.length === 0) return;
          var first = items[0];
          var last = items[items.length - 1];
          var active = document.activeElement;
          if (e.shiftKey && (active === first || items.indexOf(active) === -1)) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && (active === last || items.indexOf(active) === -1)) {
            e.preventDefault();
            first.focus();
          }
        }
      });
    }
  }

  // Expose drawer controls for cart.js (it opens the drawer on a successful add).
  window.SinqueDrawer = {
    open: openDrawer,
    close: closeDrawer,
    isOpen: isDrawerOpen
  };

  // =============================================================
  // MOBILE NAV (full-screen menu under the header bar)
  // =============================================================

  function mobileNavBgRegions() {
    return [
      document.querySelector('#main-content'),
      document.querySelector('.site-footer')
    ];
  }

  function openMobileNav() {
    var menu = document.querySelector('[data-mobile-nav]');
    var toggle = document.querySelector('[data-nav-toggle]');
    if (!menu || !toggle) return;
    menu.classList.add('is-open');
    menu.inert = false;
    menu.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    var label = toggle.querySelector('[data-nav-toggle-label]');
    if (label) label.textContent = 'Close';
    document.body.style.overflow = 'hidden';
    mobileNavBgRegions().forEach(function (el) { if (el) el.inert = true; });
    lenisStop();
    var firstLink = menu.querySelector('a');
    if (firstLink) firstLink.focus();
  }

  function closeMobileNav() {
    var menu = document.querySelector('[data-mobile-nav]');
    var toggle = document.querySelector('[data-nav-toggle]');
    if (!menu || !menu.classList.contains('is-open')) return;
    menu.classList.remove('is-open');
    menu.inert = true;
    menu.setAttribute('aria-hidden', 'true');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    var label = toggle && toggle.querySelector('[data-nav-toggle-label]');
    if (label) label.textContent = 'Menu';
    document.body.style.overflow = '';
    mobileNavBgRegions().forEach(function (el) { if (el) el.inert = false; });
    lenisStart();
    if (toggle) toggle.focus();
  }

  function isMobileNavOpen() {
    var menu = document.querySelector('[data-mobile-nav]');
    return !!(menu && menu.classList.contains('is-open'));
  }

  function wireMobileNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var menu = document.querySelector('[data-mobile-nav]');
    if (!toggle || !menu) return;
    if (!toggle.dataset.navWired) {
      toggle.dataset.navWired = '1';
      toggle.addEventListener('click', function () {
        if (menu.classList.contains('is-open')) closeMobileNav();
        else openMobileNav();
      });
    }
    // Escape + tab-trap — bound once on the persistent document.
    if (!menuKeysBound) {
      menuKeysBound = true;
      document.addEventListener('keydown', function (e) {
        if (!isMobileNavOpen()) return;
        if (e.key === 'Escape') { closeMobileNav(); return; }
        if (e.key === 'Tab') {
          var m = document.querySelector('[data-mobile-nav]');
          if (!m) return;
          var nodes = m.querySelectorAll('a[href], button:not([disabled])');
          var items = Array.prototype.filter.call(nodes, function (el) {
            return el.offsetParent !== null;
          });
          if (items.length === 0) return;
          var first = items[0];
          var last = items[items.length - 1];
          var active = document.activeElement;
          if (e.shiftKey && (active === first || items.indexOf(active) === -1)) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && (active === last || items.indexOf(active) === -1)) {
            e.preventDefault();
            first.focus();
          }
        }
      });
    }
  }

  // =============================================================
  // SIZE SELECTOR (radio-group pattern)
  // =============================================================
  //
  // Markup (per the shared contract):
  //   <div class="size-selector">
  //     <span class="size-selector__label" id="size-label" aria-live="polite">Size</span>
  //     <div class="size-selector__options" role="radiogroup" aria-labelledby="size-label">
  //       <button class="size-selector__option" role="radio" aria-checked="false"
  //               data-variant-id="..." [disabled if unavailable]>S</button>
  //       ...
  //     </div>
  //   </div>
  //
  // The selected option carries aria-checked="true" + is-selected + tabindex 0
  // (roving). cart.js reads the selected button's data-variant-id on add.

  function selectableOptions(group) {
    return Array.prototype.filter.call(
      group.querySelectorAll('.size-selector__option'),
      function (o) { return !o.disabled; }
    );
  }

  function selectOption(group, btn) {
    var all = group.querySelectorAll('.size-selector__option');
    Array.prototype.forEach.call(all, function (o) {
      var on = o === btn;
      o.classList.toggle('is-selected', on);
      o.setAttribute('aria-checked', on ? 'true' : 'false');
      // Roving tabindex: only the selected option is tabbable.
      o.tabIndex = on ? 0 : -1;
    });
    group.dataset.selectedVariant = btn.getAttribute('data-variant-id') || '';
  }

  function wireSizeSelector(form) {
    var group = form.querySelector('.size-selector__options');
    if (!group) {
      // No size group at all (single-variant product). Mark the form ready;
      // cart.js will fall back to the form's data-variant-id.
      form.dataset.sizeWired = '1';
      return;
    }
    if (form.dataset.sizeWired) return;
    form.dataset.sizeWired = '1';

    var opts = Array.prototype.slice.call(group.querySelectorAll('.size-selector__option'));
    var enabled = selectableOptions(group);

    // Single available size: auto-select it.
    if (enabled.length === 1) {
      selectOption(group, enabled[0]);
    }

    opts.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectOption(group, btn);
        clearSizeError(form);
      });
      btn.addEventListener('keydown', function (e) {
        var pool = selectableOptions(group);
        if (pool.length === 0) return;
        var idx = pool.indexOf(btn);
        // If focus is on a disabled option (it shouldn't be tabbable), bail.
        if (idx === -1) idx = 0;
        var next = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          next = (idx + 1) % pool.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          next = (idx - 1 + pool.length) % pool.length;
        } else if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          if (!btn.disabled) { selectOption(group, btn); clearSizeError(form); }
          return;
        }
        if (next >= 0) {
          e.preventDefault();
          var target = pool[next];
          selectOption(group, target);
          clearSizeError(form);
          target.focus();
        }
      });
    });
  }

  function sizeLabel(form) {
    return form.querySelector('.size-selector__label') ||
           document.getElementById('size-label');
  }

  // "Select a size" validation surfaced via the aria-live label.
  function showSizeError(form) {
    var label = sizeLabel(form);
    if (!label) return;
    label.textContent = 'Select a size';
    label.style.color = 'var(--accent)';
    if (form._sizeErrTimer) clearTimeout(form._sizeErrTimer);
    form._sizeErrTimer = setTimeout(function () {
      label.textContent = 'Size';
      label.style.color = '';
    }, 2400);
  }

  function clearSizeError(form) {
    var label = sizeLabel(form);
    if (!label) return;
    if (form._sizeErrTimer) { clearTimeout(form._sizeErrTimer); form._sizeErrTimer = null; }
    if (label.textContent === 'Select a size') {
      label.textContent = 'Size';
      label.style.color = '';
    }
  }

  // Public helpers consumed by cart.js to read/validate the selection.
  window.SinqueSize = {
    // Returns the selected variant id string, or '' if none.
    selected: function (form) {
      var group = form.querySelector('.size-selector__options');
      if (group && group.dataset.selectedVariant) return group.dataset.selectedVariant;
      // Single-variant fallback: form-level data-variant-id.
      return form.getAttribute('data-variant-id') || '';
    },
    // Whether this form even has a size group (a missing group = single variant).
    hasGroup: function (form) {
      return !!form.querySelector('.size-selector__options');
    },
    showError: showSizeError,
    clearError: clearSizeError
  };

  // =============================================================
  // BIND / RE-BIND
  // =============================================================

  function bindAll() {
    wireDrawer();
    wireMobileNav();
    document.querySelectorAll('[data-add-to-cart]').forEach(function (form) {
      wireSizeSelector(form);
    });
  }

  function boot() {
    document.documentElement.classList.add('js');
    bindAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind after theme-editor section swaps (header, product form, drawer).
  document.addEventListener('shopify:section:load', bindAll);

  // Expose a manual re-bind for cart.js / sections that swap DOM.
  window.SinqueUI = { rebind: bindAll, closeDrawer: closeDrawer, openDrawer: openDrawer };
})();
